import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
const _excluded = ["credentials", "currentHospiceBin", "currentSiteId", "personId", "sites", "siteTimezone", "authorityList", "firstName", "hbcFirm", "hospiceName", "lastName", "terminationDate", "userRoleGroups", "userRoles"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import moment from 'moment-timezone';
import { Response } from 'node-fetch';
import { ROLE_ADMIN, ROLE_HOSPICE_ADMIN } from '../constants';
import { uniqueByKey } from '../data';
import { Doctor, Nurse } from '../roleData';
import PimPgRest from './PimPgRest';
import Request from './Request';
export class Pim extends Request {
  constructor({
    url,
    pgRestUrl,
    keycloak,
    agency: _agency,
    signal
  }) {
    super({
      url,
      signal,
      keycloak
    });

    _defineProperty(this, "pgRest", void 0);

    _defineProperty(this, "keycloak", void 0);

    _defineProperty(this, "commonHeaders", {
      Authorization: '',
      'Content-Type': 'application/json',
      Accept: 'application/json'
    });

    _defineProperty(this, "flattenAndSortByKey", (users, key) => uniqueByKey( // OP-33278: alphabetical by last then first name, case-insensitive
    users.flat().sort((a, b) => {
      var _a$lastName, _b$lastName, _a$firstName, _b$firstName;

      const byLast = String((_a$lastName = a.lastName) !== null && _a$lastName !== void 0 ? _a$lastName : '').localeCompare(String((_b$lastName = b.lastName) !== null && _b$lastName !== void 0 ? _b$lastName : ''), undefined, {
        sensitivity: 'base'
      });
      if (byLast !== 0) return byLast;
      return String((_a$firstName = a.firstName) !== null && _a$firstName !== void 0 ? _a$firstName : '').localeCompare(String((_b$firstName = b.firstName) !== null && _b$firstName !== void 0 ? _b$firstName : ''), undefined, {
        sensitivity: 'base'
      });
    }), key));

    _defineProperty(this, "getPatientsByName", async name => name);

    _defineProperty(this, "getPatientList", async (siteId, tenId, agency, searchValue, eventDate, teamId) => {
      const response = await this.get({
        api: `/web/api/patients/name/v2/${searchValue}`,
        qs: {
          siteId,
          tenId,
          agency,
          activeDate: eventDate,
          teamId
        }
      });
      return 'data' in response ? response.data : response;
    });

    _defineProperty(this, "getPatientHeader", async (patientNumber, agency) => {
      let response;

      if (agency) {
        response = await this.get({
          api: `/web/api/patients/${patientNumber}/patientHeader`,
          qs: {
            agency
          }
        });
      } else {
        response = await this.get({
          api: `/web/api/patients/${patientNumber}/patientHeader`
        });
      }

      if ('entity' in response) {
        return response.entity;
      }

      return response;
    });

    _defineProperty(this, "getAllPatients", async siteId => {
      const bySite = siteId != null && siteId !== '' ? {
        // No siteFilter: 'AllSites' is intersected with the hospice sites
        // of the user's own session, which for a mumms admin is not the
        // agency being viewed and empties the result. The siteId
        // criterion alone scopes the query.
        searchCriteria: {
          siteId: Number(siteId)
        },
        queryMetaData: [{
          beanPath: 'siteId'
        }]
      } : {
        siteFilter: 'ThisSite'
      };
      const response = await this.post({
        api: `web/api/patients/listing`,
        // Hummingbird's JWT filter lets a mumms admin pick the hospice and site
        // per request through the agency and siteId query params; without
        // siteId the admin's "current site" is whatever their session holds.
        qs: siteId != null && siteId !== '' ? {
          siteId: String(siteId)
        } : {},
        data: _objectSpread({
          filterDate: Date.now(),
          patientFilter: 'AllPatients',
          offSet: -1,
          pageSize: 5000,
          statusFilter: 'ActivePatients',
          showDischargedPatientsFirst: false,
          programId: null
        }, bySite)
      });
      return response;
    });

    _defineProperty(this, "getMeds", async (patientNumber, agency) => {
      const response = await this.get({
        api: `/web/api/drfirst/medications`,
        qs: {
          patientNumber,
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getPIMUsersByRoles", (siteId, agency, ...args) => {
      const baseRequest = roleId => this.get({
        api: `/web/api/persons/name/v2`,
        qs: {
          siteId,
          roleId,
          agency
        }
      });

      return Promise.all(args.map(val => baseRequest(val)));
    });

    _defineProperty(this, "getTeamWorkers", (siteId, teamId) => {
      const response = this.get({
        api: `/web/api/persons/name/v2`,
        qs: {
          siteId,
          teamId
        }
      });
      return response;
    });

    _defineProperty(this, "getAllDoctors", async (siteId, agency) => {
      const workers = await this.getPIMUsersByRoles(siteId, agency, '-9', // PhysicianRoleId
      '-10', // MedicalDirectorRoleId
      '-11', // NursePractitionerRoleId
      '-51', // DoctorOfOsteopathyRoleId
      '-66' // HospicePhysicianRoleId
      );
      return this.flattenAndSortByKey(workers, 'personNumber');
    });

    _defineProperty(this, "getPhysicianRoles", async (siteId, agency) => {
      const physicians = await this.getPIMUsersByRoles(siteId, agency, '-9', // PhysicianRoleId
      '-10', // MedicalDirectorRoleId
      '-51', // DoctorOfOsteopathyRoleId
      '-66' // HospicePhysicianRoleId
      );

      if (!(physicians instanceof Response)) {
        return this.flattenAndSortByKey(physicians, 'personNumber');
      }

      return physicians;
    });

    _defineProperty(this, "getAllDoctorsByRole", async (siteId, agency, role) => {
      const workers = await this.getPIMUsersByRoles(siteId, agency, role);
      return this.flattenAndSortByKey(workers, 'personNumber');
    });

    _defineProperty(this, "getAllEmployees", async (siteId, agency) => {
      const workers = await this.getPIMUsersByRoles(siteId, agency, '-4', '-5', '-7', '-8');
      return this.flattenAndSortByKey(workers, 'personNumber');
    });

    _defineProperty(this, "getWorkerList", async (siteId, agency, searchValue, teamId, roleId) => {
      const response = await this.get({
        api: `/web/api/persons/name/${searchValue}`,
        qs: {
          siteId,
          agency,
          searchValue,
          teamId,
          roleId
        }
      });
      return response;
    });

    _defineProperty(this, "getAllTeams", async (agency, tenId, siteId) => {
      const response = await this.get({
        api: '/web/api/teams',
        qs: {
          agency,
          tenId,
          siteId
        }
      });
      return response;
    });

    _defineProperty(this, "getUserInfo", async (agency, RESTRICTED_ROLES, includePatientsList) => {
      const response = await this.get({
        api: '/web/api/user/currentinfo',
        qs: {
          agency,
          includePatientsList
        }
      });

      if ('personId' in response) {
        const {
          credentials: userCredentials,
          currentHospiceBin: dataHospiceBin,
          currentSiteId: siteId,
          personId: userId,
          sites: siteList,
          siteTimezone: timezone,
          authorityList,
          firstName,
          hbcFirm,
          hospiceName,
          lastName,
          terminationDate,
          userRoleGroups,
          userRoles
        } = response,
              rest = _objectWithoutProperties(response, _excluded);

        const currentHospiceBin = dataHospiceBin !== 'mummsupport' ? dataHospiceBin : null;
        const currentSite = siteList.length ? siteList.find(({
          id
        }) => id === siteId) : null;
        const siteName = currentSite ? currentSite.name : '';
        const siteNickname = currentSite ? currentSite.nickName : null;
        const nonCertifyingRoles = Array.isArray(userRoles) && Array.isArray(RESTRICTED_ROLES) ? userRoles.find(role => RESTRICTED_ROLES.includes(role)) : false;
        const isNPRole = Array.isArray(userRoles) ? userRoles.includes('NP') : false;
        const doctorRole = Array.isArray(userRoleGroups) ? userRoleGroups.includes(Doctor) : false;
        const nurseRole = Array.isArray(userRoleGroups) ? userRoleGroups.includes(Nurse) : false;
        const isMumms = Array.isArray(userRoles) && Array.isArray(authorityList) ? userRoles.includes('MAD') && authorityList.includes(ROLE_ADMIN) : false;
        const isHospiceAdmin = Array.isArray(authorityList) ? authorityList.includes(ROLE_HOSPICE_ADMIN) : false;
        const isDoctor = doctorRole && !nonCertifyingRoles;
        const isNP = doctorRole && !!isNPRole;
        const isRoleGroupsWithEditCred = doctorRole || nurseRole;

        this.keycloak.onAuthRefreshSuccess = () => {
          this.logoutOnTermination(terminationDate, timezone);
        };

        this.logoutOnTermination(terminationDate, timezone);
        return _objectSpread({
          currentHospiceBin,
          isMumms,
          isHospiceAdmin,
          firstName,
          lastName,
          userId,
          userCredentials,
          hbcFirm,
          hospiceName,
          siteList,
          siteName,
          siteNickname,
          siteId,
          isDoctor,
          isNP,
          isRoleGroupsWithEditCred,
          userRoles,
          userRoleGroups,
          authorityList,
          siteTimezone: timezone
        }, rest);
      }

      return response;
    });

    _defineProperty(this, "pgFunction", async callback => {
      if (this.pgRest) {
        return callback();
      } // eslint-disable-next-line


      console.warn('Initialize with pgRestUrl to use this function');
      return null;
    });

    _defineProperty(this, "query", (table, queryParams, schema) => this.pgFunction(() => this.pgRest.query(table, queryParams, schema)));

    _defineProperty(this, "getIdgReviews", (patientNumber, queryParams) => this.pgFunction(() => this.pgRest.getIdgReviews(patientNumber, queryParams)));

    _defineProperty(this, "getIdgMeetingAgenda", (siteId, dateFrom, dateTo) => this.pgFunction(() => this.pgRest.getIdgMeetingAgenda(siteId, dateFrom, dateTo)));

    _defineProperty(this, "addPatientToAdHocMeeting", async (details, agency) => {
      const response = await this.post({
        api: '/web/api/idg/addpatienttoadhocmeeting',
        qs: {
          agency
        },
        data: details
      });
      return response;
    });

    _defineProperty(this, "getSiteCarePlans", (siteId, fromDate, toDate) => this.pgFunction(() => this.pgRest.getSiteCarePlans(siteId, fromDate, toDate)));

    _defineProperty(this, "getSiteVisits", (siteId, fromDate, toDate) => this.pgFunction(() => this.pgRest.getSiteVisits(siteId, fromDate, toDate)));

    _defineProperty(this, "isPatientOnAdHocIDGAgenda", patientNumber => this.pgFunction(() => this.pgRest.isPatientOnAdHocIDGAgenda(patientNumber)));

    _defineProperty(this, "getPatientDetail", async (patientId, agency, tenId, siteId) => {
      const response = await this.get({
        api: `/web/api/patients/${patientId}`,
        qs: {
          agency,
          tenId,
          siteId
        }
      });
      return response;
    });

    _defineProperty(this, "getPersonDetail", async (personId, agency, tenId, siteId) => {
      const response = await this.get({
        // override the params.agency with the one passed in (if necessary
        api: `/web/api/persons/${personId}`,
        qs: {
          agency,
          tenId,
          siteId
        }
      });
      return response;
    });

    _defineProperty(this, "getPersonRoles", async (personId, agency, tenId, siteId) => {
      const response = await this.get({
        api: `/web/api/persons/${personId}/roles`,
        qs: {
          agency,
          tenId,
          siteId
        }
      });
      return response;
    });

    _defineProperty(this, "getRoleById", async (roleId, agency, tenId, siteId) => {
      const response = await this.get({
        // override the params.agency with the one passed in (if necessary
        api: `/web/api/roles/${roleId}`,
        qs: {
          agency,
          tenId,
          siteId
        }
      });
      return response;
    });

    _defineProperty(this, "getPatientEpisodes", async (agency, tenId, siteId, patIdList, teamId, startDate, endDate) => {
      const response = await this.post({
        api: '/web/api/patients/episodes/',
        qs: {
          agency,
          tenId,
          siteId,
          startDate,
          endDate
        },
        data: patIdList
      });
      return response;
    });

    _defineProperty(this, "createPatientVisits", async (patientNumber, patientVisitList, agency) => {
      const timeZone = 'UTC';
      const response = await this.post({
        api: `/web/api/patientvisits/patientnumber/${patientNumber}/visits_v2`,
        qs: {
          agency,
          timeZone
        },
        data: patientVisitList,
        method: 'POST'
      });
      return response;
    });

    _defineProperty(this, "updatePatientVisit", async (patientNumber, patientVisit, agency) => {
      const timeZone = 'UTC';
      const response = await this.post({
        api: `/web/api/patientvisits/patientnumber/${patientNumber}/visits_v2/${patientVisit.id}`,
        qs: {
          agency,
          timeZone,
          // regarding HCPCS
          isRateCalculated: 'true',
          ignoreDiagnosisForPC: 'true'
        },
        data: patientVisit,
        method: 'PUT'
      });
      return response;
    });

    _defineProperty(this, "getPatientVisits", async (patientNumber, agency) => {
      const timeZone = 'UTC';
      const response = await this.get({
        api: `/web/api/patientvisits/patientnumber/${patientNumber}/visits`,
        qs: {
          agency,
          timeZone
        }
      });
      return response;
    });

    _defineProperty(this, "getPatientVisit", async (patientId, patientVisit, agency) => {
      const timeZone = 'UTC';
      const response = await this.get({
        api: `/web/api/patientvisits/${patientId}/visits/${patientVisit.id}`,
        qs: {
          agency,
          timeZone
        }
      });
      return response;
    });

    _defineProperty(this, "deletePatientVisit", async (patientNumber, patientVisitId, agency) => {
      // https://hb.mumms.com/web/api/patientvisits/patientnumber/\{patientNumber}/visits_v2/{vId}?agency={tenantbin}&timeZone={America/Chicago}
      const timeZone = 'UTC';
      const response = await this.delete({
        api: `/web/api/patientvisits/patientnumber/${patientNumber}/visits_v2/${patientVisitId}`,
        qs: {
          agency,
          timeZone
        }
      });
      return response;
    });

    _defineProperty(this, "getVisitAttributes", async agency => {
      const response = await this.get({
        api: `/web/api/patientvisits/visitattributes`,
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getVisitId", async (patientId, visitId, agency) => {
      const timeZone = 'UTC';
      const response = await this.get({
        api: `/web/api/patientvisits/${patientId}/visits/${visitId}`,
        qs: {
          agency,
          timeZone
        }
      });
      return response;
    });

    _defineProperty(this, "createLOC", async (locData, patientNumber, agency) => {
      const response = await this.post({
        api: `/web/api/levelofcare/${patientNumber}/loc`,
        qs: {
          agency
        },
        data: locData
      });
      return response;
    });

    _defineProperty(this, "getFacilitiesForSiteLOC", async (siteId, levelOfCareType, agency) => {
      const response = await this.get({
        api: `web/api/levelofcare/facilities`,
        qs: {
          agency,
          siteId,
          levelOfCareType
        }
      });

      if (response.statusCode === 200) {
        return response.entity;
      }

      return [];
    });

    _defineProperty(this, "getListLOC", async agency => {
      const response = await this.get({
        api: `web/api/levelofcare/types`,
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "admitPatient", async (admitData, agency) => {
      const response = await this.post({
        api: `/web/api/patientepisode/admission`,
        qs: {
          agency
        },
        data: admitData
      });
      return response;
    });

    _defineProperty(this, "dischargePatient", async (dischargeData, agency) => {
      const response = await this.post({
        api: `web/api/patientepisode/discharge`,
        qs: {
          agency
        },
        data: dischargeData
      });
      return response;
    });

    _defineProperty(this, "getPatientAssignments", async (patientId, agency) => {
      const response = await this.get({
        api: `web/api/patient/assignments/${patientId}/roleassignments`,
        qs: {
          agency
        }
      });
      return response;
    });

    _defineProperty(this, "getPSOTemplate", async (siteId, agency, templateTypeShortname) => {
      const response = await this.get({
        api: `web/api/lettertemplate/standingorders_v2`,
        qs: {
          siteId,
          agency,
          templateTypeShortname
        }
      });
      return response;
    });

    _defineProperty(this, "postNewRoleAssignment", async (patientId, assignment, agency) => {
      const response = await this.post({
        api: `web/api/patient/assignments/${patientId}/roleassignments`,
        qs: {
          agency
        },
        data: assignment
      });
      return response;
    });

    _defineProperty(this, "updateRoleAssignment", async (patientId, assignment, agency) => {
      const response = await this.post({
        method: 'PUT',
        api: `web/api/patient/assignments/${patientId}/roleassignments/${assignment.id}`,
        qs: {
          agency
        },
        data: assignment
      });
      return response;
    });

    _defineProperty(this, "recordVerbalAdmitOrder", async (agency, patientId, personId, requestorpersonId, certdate) => {
      const response = await this.post({
        api: `web/api/patients/admitorder/recerts/recordverbal`,
        qs: {
          agency,
          patientId,
          personId,
          requestorpersonId,
          certdate
        },
        data: {}
      });
      return response;
    });

    _defineProperty(this, "updateCareGiver", async (patientId, id, cgInfo, agency) => {
      const response = await this.post({
        method: 'PUT',
        api: `web/api/patients/${patientId}/relations/${id}`,
        qs: {
          agency
        },
        data: cgInfo
      });
      return response;
    });

    _defineProperty(this, "checkIfPECOSApproval", async (npi, effectivedate, agency) => {
      const response = await this.get({
        qs: {
          npi,
          agency,
          effectivedate
        },
        api: 'web/api/physicians/npi/peco/eligibility'
      });
      return response;
    });

    _defineProperty(this, "checkIfPECOSEligible", async (npi, effectivedate, agency) => {
      const response = await this.post({
        method: 'POST',
        api: 'web/api/physicians/npis/peco/eligibility',
        qs: {
          agency,
          effectivedate
        },
        data: npi
      });
      return response;
    });

    this.url = new URL(url);
    this.keycloak = keycloak;

    if (_agency) {
      this.baseParams = {
        agency: _agency
      };
    }

    if (pgRestUrl) {
      this.pgRest = new PimPgRest({
        url: pgRestUrl,
        agency: _agency,
        keycloak
      });
    }

    Object.defineProperty(this.commonHeaders, 'Authorization', {
      get() {
        const {
          token
        } = keycloak;
        return `Bearer ${token}`;
      },

      set(newValue) {// need a setter
      }

    });
    this.commonHeaders.Authorization = `Bearer ${keycloak.token}`;
  }

  logoutOnTermination(terminationDate, timezone) {
    if (terminationDate && moment().isAfter(moment(terminationDate).tz(timezone).format('YYYY-MM-DD'))) {
      this.keycloak.logout();
    }
  }

}
export default Pim;